<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <img src="/aushadh-logo.png" alt="Aushadh Health Solutions" class="login-logo">
          <h1>Admin Portal</h1>
          <p class="subtitle">Sign in to access the Medizen admin dashboard</p>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="email">Email Address</label>
            <input
              v-model="form.email"
              type="email"
              id="email"
              name="email"
              placeholder="you@example.com"
              required
              :class="{ 'error': emailError }"
              @blur="validateEmail"
            />
            <span v-if="emailError" class="error-message">Please enter a valid email address</span>
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input
              v-model="form.password"
              type="password"
              id="password"
              name="password"
              placeholder="Enter your password"
              required
              :class="{ 'error': passwordError }"
            />
            <span v-if="passwordError" class="error-message">Password is required</span>
          </div>

          <div class="form-options">
            <label class="checkbox-container">
              <input v-model="form.remember" type="checkbox" />
              <span>Remember me</span>
            </label>
            <a href="#" class="forgot-link" @click.prevent="handleForgotPassword">Forgot password?</a>
          </div>

          <button type="submit" class="btn btn-primary btn-login" :disabled="isLoading">
            {{ isLoading ? 'Signing in...' : 'Sign In' }}
          </button>

          <div class="security-notice">
            <svg class="icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clip-rule="evenodd" />
            </svg>
            <span>Secure admin access only</span>
          </div>
        </form>

        <div v-if="error" class="error-banner">
          <p>{{ error }}</p>
        </div>

        <div class="divider">
          <span>Need access?</span>
        </div>

        <div class="signup-section">
          <p>This portal is for authorized administrators only</p>
          <p class="contact-admin">Contact <a href="mailto:admin@aushadhhealth.com">admin@aushadhhealth.com</a> for access</p>
        </div>

        <div class="login-footer">
          <p class="notice">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { authApi } from '@/services/api'
import { config as appConfig } from '@/config'

const router = useRouter()
const route = useRoute()

const form = ref({
  email: '',
  password: '',
  remember: false
})

const isLoading = ref(false)
const error = ref('')
const emailError = ref(false)
const passwordError = ref(false)

// Check for token in URL params (from public website redirect)
onMounted(() => {
  const token = route.query.token
  const redirect = route.query.redirect
  
  if (token) {
    try {
      // Decode and validate token
      const decoded = JSON.parse(atob(token))
      if (decoded.authenticated && decoded.timestamp) {
        // Token is valid, set it and redirect
        localStorage.setItem(appConfig.auth.tokenKey, token)
        router.push(redirect || '/dashboard')
      }
    } catch (e) {
      console.error('Invalid token:', e)
    }
  }
})

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  emailError.value = !emailRegex.test(form.value.email)
  return !emailError.value
}

const handleLogin = async () => {
  // Reset errors
  emailError.value = false
  passwordError.value = false
  error.value = ''

  // Validate
  const isEmailValid = validateEmail()
  if (!form.value.password) {
    passwordError.value = true
  }

  if (!isEmailValid || passwordError.value) {
    return
  }

  isLoading.value = true

  try {
    // Call actual auth service
    const response = await authApi.login({
      email: form.value.email,
      password: form.value.password
    })
    
    if (response.data && response.data.access_token) {
      // Store JWT token using the configured key
      localStorage.setItem(appConfig.auth.tokenKey, response.data.access_token)
      localStorage.setItem('adminUser', JSON.stringify(response.data.user))
      
      // Set token for future API calls
      authApi.setAuthToken(response.data.access_token)
      
      // Redirect to dashboard
      router.push('/dashboard')
    } else {
      error.value = 'Invalid response from authentication service.'
    }
  } catch (err) {
    console.error('Login error:', err)
    if (err.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (err.response.status === 401) {
        error.value = 'Invalid email or password. Please try again.'
      } else {
        error.value = `Server error: ${err.response.status}. Please try again later.`
      }
    } else if (err.request) {
      // The request was made but no response was received
      error.value = 'Cannot connect to authentication service. Please check your connection.'
    } else {
      // Something happened in setting up the request that triggered an Error
      error.value = err.message || 'An error occurred. Please try again.'
    }
  } finally {
    isLoading.value = false
  }
}

const handleForgotPassword = () => {
  alert('Password reset functionality coming soon. Please contact admin@aushadhhealth.com for assistance.')
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.login-container {
  width: 100%;
  max-width: 420px;
}

.login-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 3rem 2.5rem;
}

.login-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.login-logo {
  height: 60px;
  width: auto;
  margin-bottom: 1.5rem;
}

.login-header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #64748b;
  font-size: 1rem;
}

.login-form {
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-group input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-group input.error {
  border-color: #ef4444;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.875rem;
  color: #475569;
}

.checkbox-container input {
  margin-right: 0.5rem;
  cursor: pointer;
}

.forgot-link {
  font-size: 0.875rem;
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.forgot-link:hover {
  text-decoration: underline;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  font-size: 1rem;
}

.btn-primary {
  background: #2563eb;
  color: white;
  width: 100%;
}

.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.security-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.75rem;
}

.security-notice .icon {
  width: 16px;
  height: 16px;
  color: #94a3b8;
}

.error-banner {
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
}

.error-banner p {
  color: #dc2626;
  font-size: 0.875rem;
  text-align: center;
  margin: 0;
}

.divider {
  text-align: center;
  margin: 2rem 0;
  position: relative;
}

.divider::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  background: #e2e8f0;
}

.divider span {
  background: white;
  padding: 0 1rem;
  position: relative;
  color: #64748b;
  font-size: 0.875rem;
}

.signup-section {
  text-align: center;
}

.signup-section p {
  color: #475569;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.contact-admin {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.contact-admin a {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.contact-admin a:hover {
  text-decoration: underline;
}

.login-footer {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.notice {
  text-align: center;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 480px) {
  .login-card {
    padding: 2rem 1.5rem;
  }
  
  .login-header h1 {
    font-size: 1.75rem;
  }
  
  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}
</style>